import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useRunCompletionRefresh } from "@/features/credentials/components/primary-source-runs/use-run-completion-refresh";
import { useRunHistory } from "@/features/credentials/components/primary-source-runs/use-run-history";
import {
  ACTIVE_RUN_POLL_MS,
  isRunActive,
  isRunCancelled,
  type RunScope,
} from "@/features/credentials/components/primary-source-runs/types";

/** The provider's run history and the report on the selected run, which is the
 * latest run unless the page names an earlier one. A cancelled run never
 * replaces the run before it, matching the roster's status and the API's
 * review rules, so it is shown only when picked from the history. */
export function useProviderVerification({
  scope,
  runId,
  enabled,
}: {
  scope: RunScope;
  runId?: string;
  enabled: boolean;
}) {
  const trpc = useTRPC();
  const history = useRunHistory({ scope, enabled });
  const runs = history.data?.pages.flatMap((page) => page.runs) ?? [];
  const latestRun = runs.find((run) => !isRunCancelled(run));
  const selectedRunId = runId ?? latestRun?.id;
  const report = useQuery({
    ...trpc.psv.getRun.queryOptions({ ...scope, runId: selectedRunId ?? "" }),
    enabled: enabled && !!selectedRunId,
    refetchInterval: (query) =>
      isRunActive(query.state.data?.run) ? ACTIVE_RUN_POLL_MS : false,
  });
  const completedRunId =
    latestRun && !isRunActive(latestRun) ? latestRun.id : null;
  const completedReportId =
    report.data && !isRunActive(report.data.run) ? report.data.run.id : null;
  useRunCompletionRefresh({ scope, completedRunId, completedReportId });
  return {
    history,
    runs,
    latestRun,
    selectedRunId,
    hasActiveRun: runs.some(isRunActive),
    report,
  };
}
